import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { bindActionCreators } from 'redux';
import Helmet from 'react-helmet';
import { createStructuredSelector } from 'reselect';
import { Link, useLocation, useHistory } from 'react-router-dom';
import {
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableFooter,
  Button,
  LinearProgress,
} from '@material-ui/core';

import { Features } from 'utils/features';
import TabItem from 'retired/TabItem';
import { TableBox } from 'retired/Box';
import { selectForms } from 'entities/form/selectors';
import * as NotificationActions from 'containers/Notification/actions';
import { useInjectReducer } from 'utils/injectReducer';
import { useInjectEpics } from 'utils/injectEpics';
import { selectUser } from 'entities/user/selectors';
import { Privileges } from 'utils/privileges';
import { PageBannerContainer } from 'components/PageBannerContainer/PageBannerContainer';
import FormsPageBanner from 'assets/pageBanners/forms.avif';

import * as Actions from './actions';
import { selectError, selectLoading } from './selectors';
import epics from './epics';
import reducer from './reducer';

const FormItem = ({ form, isSurvey }) => (
  <TableRow>
    <TableCell>
      <Link to={`/${isSurvey ? 'surveys' : 'forms'}/${form.id}`} id={form.id}>
        {form.name}
      </Link>
      {form.subtitle ? ` (${form.subtitle})` : ''}
    </TableCell>
    <TableCell>{form.responsesCount}</TableCell>
    {!isSurvey && <TableCell>{form.deeds && form.deeds.length}</TableCell>}
  </TableRow>
);
FormItem.propTypes = {
  form: PropTypes.object,
  isSurvey: PropTypes.bool,
};

const key = 'formsPage';
const FormsPage = ({ loading, forms, error, actions, user }) => {
  const history = useHistory();
  const canAccessSurveys =
    user?.hasPrivilege(Privileges.SURVEY_MANAGE) && user?.hasFeature(Features.SURVEYS);
  const location = useLocation();
  const isSurvey = location.pathname === '/surveys';

  useEffect(() => {
    if (
      user &&
      !(
        (canAccessSurveys || user?.hasPrivilege(Privileges.FORM_MANAGE)) &&
        user.hasFeature(Features.VOLUNTEERING)
      )
    ) {
      history.replace('/');
    }
  }, [history, user, canAccessSurveys]);

  useInjectReducer({ key, reducer });
  useInjectEpics({ key, epics });
  useEffect(() => {
    const formType = isSurvey ? 'survey' : 'form';
    const responsesCountOnly = true;

    const hasStoredSurveys = forms?.some((f) => f.isSurvey);
    const hasStoredForms = forms?.some((f) => !f.isSurvey);
    if (hasStoredSurveys && hasStoredForms) {
      return;
    }
    actions.initAction(responsesCountOnly, formType);
  }, [actions, isSurvey]);

  const filteredForms = forms.filter((f) => (isSurvey ? f.isSurvey : !f.isSurvey));
  return (
    <>
      <Helmet title={`Forms${canAccessSurveys ? ' & Surveys' : ''}`} />
      <PageBannerContainer color="#FFE9E9" backgroundImage={FormsPageBanner}>
        <h1>Forms{canAccessSurveys ? ' & Surveys' : ''}</h1>
        <div style={{ marginLeft: 'auto' }}>
          <Link to={isSurvey ? '/surveys/edit/new' : '/forms/edit/new'}>
            <Button color="primary" variant="outlined" size="large">
              Create new {isSurvey ? 'Survey' : 'Form'}
            </Button>
          </Link>
        </div>
      </PageBannerContainer>
      {loading ? <LinearProgress /> : <div style={{ height: 4 }} />}
      {!loading && (
        <>
          {canAccessSurveys && (
            <>
              <TabItem
                isActive={location.pathname === '/forms'}
                onClick={() => {
                  history.push('/forms');
                }}
              >
                Forms
              </TabItem>
              <TabItem
                isActive={location.pathname === '/surveys'}
                onClick={() => {
                  history.push('/surveys');
                }}
              >
                Surveys
              </TabItem>
            </>
          )}
          {filteredForms.size ? (
            <>
              <div
                style={{
                  paddingTop: 15,
                  display: 'table',
                  width: '100%',
                }}
              >
                <TableBox>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Name</TableCell>
                        <TableCell>Number of responses</TableCell>
                        {!isSurvey && <TableCell>Number of deeds</TableCell>}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredForms
                        .filter((f) => (isSurvey ? f.isSurvey : !f.isSurvey))
                        .map((form) => <FormItem key={form.id} form={form} isSurvey={isSurvey} />)
                        .toArray()}
                    </TableBody>
                    <TableFooter>
                      <TableRow>
                        <TableCell
                          size="medium"
                          colSpan={100}
                          style={{ color: '#0000008a', fontSize: '0.875rem' }}
                        >
                          Total: {filteredForms.size}
                        </TableCell>
                      </TableRow>
                    </TableFooter>
                  </Table>
                </TableBox>
              </div>
            </>
          ) : isSurvey ? (
            <>
              <h2>
                Build custom surveys for your volunteers to fill out directly in the Deed app.
                Collect all the info your organization requires, seamlessly.
              </h2>
              <ol>
                <li>Build your first survey by selecting “Create new survey” above.</li>
                <li>Name your survey and add as many desired questions as needed.</li>
                <li>Your survey will be stored on the “surveys” tab for future use.</li>
                <li>
                  When creating a Deed, you may choose your newly created survey as a requirement
                  for attending the volunteer event.
                </li>
                <li>
                  Volunteers will fill out the survey prior to being able to attend. All survey
                  responses are stored on the volunteer’s profile in the directory.
                </li>
              </ol>
            </>
          ) : (
            <>
              <h2>
                Build custom forms for your volunteers to fill out directly in the Deed app. Collect
                all the info your organization requires, seamlessly.
              </h2>
              <ol>
                <li>Build your first form by selecting “Create new form” above.</li>
                <li>Name your form and add as many desired questions as needed.</li>
                <li>Your form will be stored on the “Forms” tab for future use.</li>
                <li>
                  When creating a Deed, you may choose your newly created form as a requirement for
                  attending the volunteer event.
                </li>
                <li>
                  Volunteers will fill out the form prior to being able to attend. All form
                  responses are stored on the volunteer’s profile in the directory.
                </li>
              </ol>
            </>
          )}
        </>
      )}
      {error && <div>There was an error loading {isSurvey ? 'surveys' : 'forms'}</div>}
    </>
  );
};

FormsPage.propTypes = {
  loading: PropTypes.bool,
  error: PropTypes.oneOfType([PropTypes.object, PropTypes.bool]),
  forms: PropTypes.object,
  user: PropTypes.object,
  actions: PropTypes.object,
};

const withConnect = connect(
  createStructuredSelector({
    forms: selectForms,
    loading: selectLoading,
    error: selectError,
    user: selectUser,
  }),
  (dispatch) => ({
    actions: bindActionCreators({ ...Actions, ...NotificationActions }, dispatch),
  })
);

export default withConnect(FormsPage);
